import React, { useCallback, useEffect, useRef, useState } from "react";
import { SafeAreaView, View, StyleSheet, ScrollView, Platform } from "react-native";
import { useDispatch, useSelector } from "react-redux";
import { useFocusEffect, useNavigation } from "@react-navigation/native";
import { Grid, Container, Row, Column } from "ui-grid";
import automationTestingLocators from "./utils/automation-testing-locators.json";
import { RootState } from "../../redux/reducers";
import { DomusoApiService } from "../../api/DomusoApiService";
import { translate } from "../../i18n";
import { theme, Button, ButtonType, ErrorWarningBottomSheet } from "ui-components";
import {
  setDefaultPaymentSourceUpdated,
  setPaymentStarted,
} from "../../redux/actions/actionCreators";
import { PaymentSourceType, PaymentType } from "../../enums";
import PaymentMethods from "../../components/new/payment-flow/payment-methods";
import { HomeScreenProps } from "../../navigation/home-stack/home-stack";
import { usePaymentMethodInformation } from "../../hooks/usePaymentMethodInformation";
import Big from "big.js";
import { addStartOfDayInformationWithTimezone } from "../../utils/date";
import { isAvsReady } from "../../utils/vantiv";
import { Footer } from "../../components/new/footer/footer";
import { BottomNav } from "../../components/layout/bottom-nav";
import { paymentMethodsPaymentFlow } from "./utils/business-logic-payment-methods";
import { PaymentFlowProgressIndicator } from "../../components/new/payment-flow/payment-flow-progress-indicator";
import { domusoResponsiveStyleHelper } from "./utils/domuso-responsive";
import { VerifyBillingAddressNew } from "../../components/new/verify-billing-address/verify-billing-address";

const { CREDIT_CARD, BANK_ACCOUNT } = PaymentSourceType;

enum ErrorWarningBottomSheetType {
  error = "error",
  warning = "warning",
}
type ErrorWarningBottomSheetTypes = `${ErrorWarningBottomSheetType}`;

interface ErrorMsgObjectProps {
  title?: string;
  body?: string;
  errorType?: ErrorWarningBottomSheetTypes;
}
export const PaymentFlowPaymentMethod = ({ route }: HomeScreenProps) => {
  const logger = useSelector((state: RootState) => state.logger);
  const navigation = useNavigation();
  const dispatch = useDispatch();

  const cognitoUser = useSelector((state: RootState) => state.auth.cognitoUser);
  const [isLoading, setLoading] = useState(false);
  const [errorMsgObject, setErrorMsgObject] = useState<ErrorMsgObjectProps>({});
  const errorPopupRef = useRef<any>();
  const warningPopupRef = useRef<any>();
  const scrollViewRef = useRef<ScrollView>(null);

  const verifyBillAddressPopupRef = useRef<any>();
  const { params } = route;
  const {
    propertyUnit,
    reservationId,
    currentPropertyProfile,
    allowedPayments,
    fromAutopay,
    finalPaymentAmounts,
    finalPaymentDate,
  } = params;
  let {
    certified_start_day,
    certified_end_day,
    allow_ach_payments,
    allow_card_payments = false,
    allow_domuso_cash = false,
  } = currentPropertyProfile || {};
  certified_start_day = certified_start_day ? certified_start_day : 1;
  certified_end_day = certified_end_day ? certified_end_day : 6;
  const preSelectedPaymentSourceId = useSelector((state) => state.payment.paymentSourceId);
  const {
    paymentSources,
    firstValidSource,
    error: paymentSourcesError,
  } = usePaymentMethodInformation({
    certifiedDateRange: { certified_end_day, certified_start_day },
    allowedPayments,
    preSelectedPaymentSourceId,
    keepInactive: false,
    allowACH: !!allow_ach_payments,
    allowCC: !!allow_card_payments,
  });
  const [selected, emitSelected] = useState<any>();
  const [preSelected, emitPreSelected] = useState<any>();
  const [isDuplicatePayment, setIsDuplicatePayment] = useState(false);
  const [hasUserCCPayBlock, setHasUserCCPayBlock] = useState(false);
  let [toggleForCardBottomSheet, setToggleForCardBottomSheet] = useState(false);
  let [finalAmountsState, setFinalAmountsState] = useState();
  const [certifiedACHSelected, setCertifiedACHSelected] = useState(false);

  const service = new DomusoApiService(cognitoUser, dispatch);
  const propertyFees = useSelector((state: RootState) => state.reservation.propertyFees);

  const scrollToTop = () => {
    if (Platform.OS === "web") {
      window?.scroll({ top: 0 });
    } else {
      scrollViewRef?.current?.scrollTo({ y: 0 });
    }
  };
  const setDefaultPaymentSource = () => {
    let paymentSource = firstValidSource;
    if (paymentSources && paymentSources.length && preSelectedPaymentSourceId) {
      paymentSource = paymentSources.find((source) => source.id === preSelectedPaymentSourceId);
    }
    if (paymentSource) {
      if (paymentSource.type == BANK_ACCOUNT && allow_ach_payments) {
        scrollToTop();
        emitSelected({ ...paymentSource, paymentType: paymentSource.type });
        emitPreSelected({ ...paymentSource, paymentType: paymentSource.type });
      }
      if (paymentSource.type == CREDIT_CARD && allow_card_payments) {
        if (!isCertifiedPaymentAllowed()) {
          return;
        }
        if (!hasUserCCPayBlock) {
          scrollToTop();
          emitSelected({ ...paymentSource, paymentType: paymentSource.type });
          emitPreSelected({ ...paymentSource, paymentType: paymentSource.type });
        }
      }
    }
  };

  const layoutEffect = useCallback(() => {
    dispatch(setPaymentStarted(reservationId));
    service
      .checkUserCCPayBlock({ reservationId })
      .then((blockedRes) => {
        setHasUserCCPayBlock(!!blockedRes.blocked);
      })
      .catch((e) => {
        logger.error(e);
      });
  }, []);
  useFocusEffect(layoutEffect);

  const updateSelected = (value) => {
    dispatch(setDefaultPaymentSourceUpdated(value.id));
    setIsDuplicatePayment(false);
    emitSelected(value);
    scrollToTop();

    if (value?.type === PaymentType.DOMUSO_CASH && value?.sourceType === BANK_ACCOUNT) {
      setCertifiedACHSelected(true);
    } else {
      setCertifiedACHSelected(false);
    }
  };

  const isCertifiedPaymentAllowed = () => {
    if (!allow_domuso_cash) {
      return false;
    }

    return true;
  };

  const getAmounts = () => {
    return finalPaymentAmounts;
  };

  const getPaymentDate = () => {
    const paymentDate = finalPaymentDate;
    return paymentDate;
  };

  const getDisabled = () => {
    // if()
    // Could be more conditions in the future
    return (
      isLoading ||
      !selected ||
      isDuplicatePayment ||
      (selected?.type == CREDIT_CARD && !isCertifiedPaymentAllowed())
    );
  };

  const showUnexpectedError = (error?: any) => {
    setLoading(false);
    if (
      error &&
      (error == "networkError" ||
        error?.message == "Failed to fetch" ||
        error?.message == "Network request failed")
    ) {
      setErrorMsgObject({
        title: translate("paymentFlow.paymentMethod.connectionErrorTitle") as string,
        body: translate("paymentFlow.paymentMethod.connectionErrorMessage") as string,
        errorType: "error",
      });
      errorPopupRef?.current?.open();
      return;
    } else {
      setErrorMsgObject({
        title: translate("paymentFlow.paymentMethod.error") as string,
        body: translate("paymentFlow.paymentMethod.unexpectedError") as string,
        errorType: "error",
      });
      errorPopupRef?.current?.open();
      return;
    }
  };
  const onSubmit = async (data?: any) => {
    try {
      setLoading(true);
      const amounts = getAmounts();
      const paymentDate = getPaymentDate();
      const pstDate = addStartOfDayInformationWithTimezone(paymentDate);
      let { type, id } = selected;

      const res = await service
        .checkDuplicatePayment({
          amount: amounts?.amount,
          payment_type: type,
          date_iso: pstDate,
          payment_source_id: id,
          reservationId,
          paymentSourceType: certifiedACHSelected ? BANK_ACCOUNT : null,
        })
        .catch((e) => {
          logger.error(e);
        });

      if (res === undefined || res === null) {
        showUnexpectedError("networkError");
        return;
      }
      if (res && res?.hasDuplicatePayment) {
        setLoading(false);
        setErrorMsgObject({
          title: translate("paymentFlow.paymentMethod.duplicatePayment") as string,
          body: translate("paymentFlow.paymentMethod.duplicatePaymentBody") as string,
          errorType: "error",
        });
        errorPopupRef?.current?.open();
        return setIsDuplicatePayment(true);
      }

      // check for similar payments
      const resSimilarPaymentCheck = await service
        .checkSimilarPayment({
          amount: amounts?.amount,
          payment_type: type,
          date_iso: pstDate,
          payment_source_id: id,
          reservationId,
        })
        .catch((e) => {
          logger.error(e);
        });
      if (resSimilarPaymentCheck === undefined || resSimilarPaymentCheck === null) {
        showUnexpectedError("networkError");
        return;
      }
      if (
        resSimilarPaymentCheck &&
        resSimilarPaymentCheck?.paymentSchedulesWithDesiredAttributes?.length > 0
      ) {
        setLoading(false);
        const paymentDate = new Date(
          resSimilarPaymentCheck?.paymentSchedulesWithDesiredAttributes[0]?.schedule_date
        )?.toLocaleDateString("en-us", {
          year: "numeric",
          month: "short",
          day: "numeric",
        });
        setErrorMsgObject({
          title: translate("paymentFlow.paymentMethod.duplicatePayment") as string,
          body: `${translate(
            "paymentFlow.paymentMethod.similarPaymentBodyPart1"
          )} ${paymentDate}. ${translate("paymentFlow.paymentMethod.similarPaymentBodyPart2")}`,
          errorType: "warning",
        });
        warningPopupRef?.current?.open();
        return;
      }
      setLoading(false);

      if (certifiedACHSelected) {
        navigateToCertifiedPaymentFlow();
      } else {
        navigateToPaymentSummaryScreen();
      }
      return;
    } catch (error) {
      showUnexpectedError(error);
    }
  };

  const navigateToPaymentSummaryScreen = () => {
    const amounts = getAmounts();
    const paymentDate = getPaymentDate();
    const pstDate = addStartOfDayInformationWithTimezone(paymentDate);
    let { last_four_digits, type, id, card_type, bank_account_type } = selected;

    const finalAmounts: any = {
      amount: Big(amounts?.amount || 0),
    };
    if (type === PaymentSourceType.BANK_ACCOUNT) {
      finalAmounts.platformFee = Big(propertyFees?.bankAccount?.fee);
      finalAmounts.total = finalAmounts.amount.add(finalAmounts.platformFee);
      card_type = bank_account_type;
    } else if (type === PaymentSourceType.CREDIT_CARD) {
      finalAmounts.platformFee = propertyFees?.ccDebitCard.fee;
      finalAmounts.total = finalAmounts.amount.add(finalAmounts.platformFee);
    }
    setFinalAmountsState(finalAmounts);
    setLoading(false);
    if (type === PaymentSourceType.CREDIT_CARD && !isAvsReady(selected)) {
      verifyBillAddressPopupRef?.current?.open();
      setToggleForCardBottomSheet(!toggleForCardBottomSheet);
      return;
    }
    navigation.navigate("PaymentFlowPaymentSummary", {
      ...params,
      propertyUnit,
      reservationId,
      paymentDate: pstDate,
      authorizationDate: type === PaymentSourceType.CREDIT_CARD ? paymentDate : null,
      amounts: finalAmounts,
      paymentSourceId: id,
      paymentScheduleType: fromAutopay ? "RECURRING" : "ONE_SHOT",
      last4Digits: last_four_digits,
      paymentSourceType: type,
      cardType: card_type,
      isCertified: false,
      selectedPaymentMethod: selected,
    });
    return;
  };

  const navigateToCertifiedPaymentFlow = () => {
    const paymentDate = getPaymentDate();
    const pstDate = addStartOfDayInformationWithTimezone(paymentDate);
    const amounts = getAmounts();
    const finalAmounts: any = {
      amount: Big(amounts?.amount || 0),
    };

    //Reset state since certifie BA card doesn't have a selected indicator
    setCertifiedACHSelected(false);
    dispatch(setDefaultPaymentSourceUpdated(null));
    emitSelected(null);
    emitPreSelected(null);

    navigation.navigate("PaymentFlowPaymentMethodCertifiedACH", {
      ...params,
      paymentDate: pstDate,
      propertyUnit,
      reservationId,
      amounts: finalAmounts,
    });
    return;
  };

  const onContinue = async () => {
    let isAllowed = isCertifiedPaymentAllowed();
    if (selected?.type == CREDIT_CARD && !isAllowed) {
      emitSelected(null);
      emitPreSelected(null);
    } else {
      onSubmit();
    }
  };

  useEffect(() => {
    if (preSelectedPaymentSourceId) {
      setDefaultPaymentSource();
    }
  }, [firstValidSource]);

  useEffect(() => {
    if (paymentSourcesError) {
      showUnexpectedError(paymentSourcesError);
    }
  }, [paymentSourcesError]);

  let paymentMethodsPaymentFlowAvailability = paymentMethodsPaymentFlow({
    shouldDisableCC: hasUserCCPayBlock,
    ...params,
  });

  //When user selects Certified Bank Account we need to check for duplicates as well
  //This effect will trigger the onContinue function as any other payments so it can go through duplicate detection check.
  useEffect(() => {
    if (certifiedACHSelected) {
      const transitionCertifiedACH = async () => {
        await onContinue();
      };

      transitionCertifiedACH();
    }
  }, [certifiedACHSelected]);

  return (
    <SafeAreaView style={styles.container}>
      <View style={{ marginVertical: 20 }}>
        <PaymentFlowProgressIndicator currentPosition={1} />
      </View>
      <ScrollView
        ref={scrollViewRef}
        style={styles.scrollView}
        contentContainerStyle={styles.scrollableArea}>
        <>
          <Grid>
            <Container>
              <Row>
                <Column span={{ xs: false, sm: 2, md: 2, lg: 2 }} />
                <Column style={[styles.cardTop]} span={{ xs: 12, sm: 8, md: 8, lg: 8 }}></Column>
                <Column span={{ xs: false, sm: 2, md: 2, lg: 2 }} />
              </Row>
            </Container>
          </Grid>
          <View {...automationTestingLocators.paymentFlowPaymentMethod.paymentMethods}>
            <PaymentMethods
              paymentMethodsAvailabilityObject={paymentMethodsPaymentFlowAvailability}
              paymentSources={paymentSources}
              onPressItem={(item: any) => {
                updateSelected(item);
              }}
              selected={selected}
              preSelected={preSelected}
              finalAmounts={getAmounts()}
              paymentDate={getPaymentDate()}
              paymentSourcesError={paymentSourcesError}
              {...params}
            />
          </View>
        </>
      </ScrollView>
      <BottomNav backgroundColor={theme.colors.white}>
        <View style={{ marginTop: 15 }} />
        <Row>
          <Column span={{ xs: false, sm: 3, md: 3, lg: 3 }} />
          <Column span={{ xs: 12, sm: 6, md: 6, lg: 6 }}>
            <Button
              text={translate("paymentFlow.paymentMethod.next") as string}
              onPress={() => {
                onContinue();
              }}
              type={ButtonType.primary}
              disabled={getDisabled()}
              loading={isLoading}
              {...automationTestingLocators.paymentFlowPaymentMethod.paymentFlowNextButton}
            />
          </Column>
          <Column span={{ xs: false, sm: 3, md: 3, lg: 3 }} />
        </Row>
      </BottomNav>
      <Footer />
      <ErrorWarningBottomSheet
        ref={errorPopupRef}
        title={errorMsgObject?.title}
        body={errorMsgObject?.body}
        height={170}
        alertType={errorMsgObject?.errorType}
        showButtons={false}
        {...automationTestingLocators.paymentFlowPaymentMethod.errorAlert}
      />
      <ErrorWarningBottomSheet
        ref={warningPopupRef}
        title={errorMsgObject?.title}
        body={errorMsgObject?.body}
        height={230}
        alertType={"warning"}
        leftButtonText={translate("paymentFlow.paymentMethod.cancelPaymentButtonText") as string}
        rightButtonText={translate("paymentFlow.paymentMethod.allowPaymentButtonText") as string}
        leftButtonType={"secondary"}
        leftButtonStyle={{ marginTop: 20 }}
        rightButtonStyle={{ marginTop: 20 }}
        onPressLeftButton={() => {
          warningPopupRef?.current?.close();
          navigation.navigate("Home");
        }}
        onPressRightButton={() => {
          warningPopupRef?.current?.close();
          if (certifiedACHSelected) {
            navigateToCertifiedPaymentFlow();
          } else {
            navigateToPaymentSummaryScreen();
          }
        }}
        {...automationTestingLocators.paymentFlowPaymentMethod.warningAlert}
      />
      <VerifyBillingAddressNew
        ref={verifyBillAddressPopupRef}
        {...params}
        propertyUnit={propertyUnit}
        reservationId={reservationId}
        paymentDate={addStartOfDayInformationWithTimezone(getPaymentDate())}
        authorizationDate={
          selected?.type === PaymentSourceType.CREDIT_CARD ? getPaymentDate() : null
        }
        amounts={finalAmountsState}
        paymentSourceId={selected?.id}
        paymentScheduleType={fromAutopay ? "RECURRING" : "ONE_SHOT"}
        last4Digits={selected?.last_four_digits}
        paymentSourceType={selected?.type}
        cardType={selected?.card_type}
        isCertified={false}
        selectedPaymentMethod={selected}
        toggleForCardBottomSheet={toggleForCardBottomSheet}
      />
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    ...domusoResponsiveStyleHelper({
      webPhone: {
        backgroundColor: "#FFFFFF",
      },
      webTablet: {
        backgroundColor: "#FAFAFA",
      },
      webDesktop: {
        backgroundColor: "#FAFAFA",
      },
      iosPhone: {
        backgroundColor: "#FFFFFF",
      },
      iosTablet: {
        backgroundColor: "#FAFAFA",
      },
      androidPhone: {
        backgroundColor: "#FFFFFF",
      },
      androidTablet: {
        backgroundColor: "#FAFAFA",
      },
    }),
  },
  scrollView: {
    flexGrow: 0,
  },
  scrollableArea: {
    ...domusoResponsiveStyleHelper({
      webPhone: { backgroundColor: "#FFFFFF", paddingBottom: 10 },
      webTablet: { backgroundColor: "#FAFAFA", paddingBottom: 10 },
      webDesktop: { backgroundColor: "#FAFAFA", paddingBottom: 10 },
      iosPhone: { backgroundColor: "#FFFFFF", paddingBottom: 200 },
      iosTablet: { backgroundColor: "#FAFAFA", paddingBottom: 10 },
      androidPhone: { backgroundColor: "#FFFFFF", paddingBottom: 200 },
      androidTablet: { backgroundColor: "#FAFAFA", paddingBottom: 10 },
    }),
  },
  cardTop: {
    paddingVertical: 8,
    ...domusoResponsiveStyleHelper({
      webPhone: { backgroundColor: "#FAFAFA" },
      webTablet: {
        backgroundColor: "#FFFFFF",
        paddingLeft: 40,
        paddingRight: 40,
        borderTopRightRadius: 10,
        borderTopLeftRadius: 10,
        borderTopWidth: 1,
        borderTopColor: "rgba(102, 102, 102, 0.1)",
        borderLeftWidth: 1,
        borderLeftColor: "rgba(102, 102, 102, 0.1)",
        borderRightWidth: 1,
        borderRightColor: "rgba(102, 102, 102, 0.1)",
      },
      webDesktop: {
        backgroundColor: "#FFFFFF",
        paddingLeft: 40,
        paddingRight: 40,
        borderTopRightRadius: 10,
        borderTopLeftRadius: 10,
        borderTopWidth: 1,
        borderTopColor: "rgba(102, 102, 102, 0.1)",
        borderLeftWidth: 1,
        borderLeftColor: "rgba(102, 102, 102, 0.1)",
        borderRightWidth: 1,
        borderRightColor: "rgba(102, 102, 102, 0.1)",
      },
      iosPhone: { backgroundColor: "#FFFFFF" },
      iosTablet: {
        backgroundColor: "#FFFFFF",
        paddingLeft: 40,
        paddingRight: 40,
        borderTopRightRadius: 10,
        borderTopLeftRadius: 10,
        borderTopWidth: 1,
        borderTopColor: "rgba(102, 102, 102, 0.1)",
        borderLeftWidth: 1,
        borderLeftColor: "rgba(102, 102, 102, 0.1)",
        borderRightWidth: 1,
        borderRightColor: "rgba(102, 102, 102, 0.1)",
      },
      androidPhone: { backgroundColor: "#FFFFFF" },
      androidTablet: {
        backgroundColor: "#FFFFFF",
        paddingLeft: 40,
        paddingRight: 40,
        borderTopRightRadius: 10,
        borderTopLeftRadius: 10,
        borderTopWidth: 1,
        borderTopColor: "rgba(102, 102, 102, 0.1)",
        borderLeftWidth: 1,
        borderLeftColor: "rgba(102, 102, 102, 0.1)",
        borderRightWidth: 1,
        borderRightColor: "rgba(102, 102, 102, 0.1)",
      },
    }),
  },
  errorRow: {
    ...domusoResponsiveStyleHelper({
      webPhone: {},
      webTablet: {
        paddingLeft: 40,
        paddingRight: 40,

        borderLeftWidth: 1,
        borderLeftColor: "rgba(102, 102, 102, 0.1)",
        borderRightWidth: 1,
        borderRightColor: "rgba(102, 102, 102, 0.1)",
      },
      webDesktop: {
        paddingLeft: 40,
        paddingRight: 40,

        backgroundColor: "white",

        borderLeftWidth: 1,
        borderLeftColor: "rgba(102, 102, 102, 0.1)",
        borderRightWidth: 1,
        borderRightColor: "rgba(102, 102, 102, 0.1)",
      },
      iosPhone: {},
      iosTablet: {
        paddingLeft: 40,
        paddingRight: 40,

        backgroundColor: "white",

        borderLeftWidth: 1,
        borderLeftColor: "rgba(102, 102, 102, 0.1)",
        borderRightWidth: 1,
        borderRightColor: "rgba(102, 102, 102, 0.1)",
      },
      androidPhone: {},
      androidTablet: {
        paddingLeft: 40,
        paddingRight: 40,

        backgroundColor: "white",

        borderLeftWidth: 1,
        borderLeftColor: "rgba(102, 102, 102, 0.1)",
        borderRightWidth: 1,
        borderRightColor: "rgba(102, 102, 102, 0.1)",
      },
    }),
  },
});

export default PaymentFlowPaymentMethod;
